import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { Box, Stack, Tooltip } from '@mui/material';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { FiPlus } from 'react-icons/fi';
import { BiSolidEdit } from 'react-icons/bi';
import { cloneDeep, find, findIndex } from 'lodash';
import { notification, Popover } from 'antd';

import Modal from '../../../components/uiComponent/modal/index';
import Button from '../../../components/uiComponent/button/index';

import AddSkuModal from './AddSkuModal';
import { updatePOShipQty, updatePOShipQuantity, updateWarehouseStock, DeletePoSkuMapping } from '../../../slices/po';

import { InnerTableWrapper, PoDetailsWrapper } from './style';
import {STORAGE_ICON_URL, STORAGE_IMAGE_URL} from '../../../constants/storage';
const deleteIcon = `${STORAGE_IMAGE_URL}/newImages/delete-outlined.svg`;
const WarningIcon = `${STORAGE_ICON_URL}/warning-modal-icon.svg`;


const InfoIcon = `${STORAGE_ICON_URL}/info-black.svg`;

const PoRowDetails = (props) => {
  const dispatch = useDispatch();

  const {
    poDetails,
    poItems,
    deletingMappingPoAndSku
  } = useSelector((state) => state.pos || {});

  const { poId } = useParams();

  const [addSkuModal, setAddSkuModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSKU, setSelectedSKU] = useState(null);

  const { orderItems, setOrderItems, data, onParentSelectionChange } = props || {};
  const { product, id: po_item_id, seller_skus, received_quantity = 0 } = data || {};
  const { id: product_id } = product || {};
  const {
    status
  } = poDetails || {};

  const handleUpdateShipQty = (params) => {
    const { newValue, oldValue = 0, data, node } = params || {};
    const { id } = data || {};

    if (!newValue || isNaN(newValue) || (newValue < 0) || (newValue.trim() === '') || (newValue === oldValue)) {
      notification.warning({
        message: 'Please enter a valid ship quantity!',
        top: 60
      });
      return false;
    }

    const poItemIndex = findIndex(poItems, (item) => item?.product?.id === product_id);
    const { seller_skus } = poItems[poItemIndex] || {};

    const skuIndex = node.rowIndex;

    setOrderItems(orderItems => {
      const orderItemIndex = findIndex(orderItems, (item) => item.product_id === product_id);

      if (orderItemIndex === -1) {
        const skus = seller_skus?.map(({ id: sku_id }) => ({
          sku_id,
          po_sku_mappings_v2: { ship_quantity: 0 }
        })) || [];
        
        if (skus[skuIndex]) {
          skus[skuIndex].po_sku_mappings_v2.ship_quantity = Number(newValue);
        }
        
        const isHighlighted = false;
        return [...orderItems, { product_id, skus, isHighlighted }];
      } else {
        const orderItemsClone = cloneDeep(orderItems);
        const { skus } = orderItemsClone[orderItemIndex] || {};
        const skuItemIndex = findIndex(skus, (item) => item.sku_id === id);
        
        if (skuItemIndex !== -1) {
          if (!orderItemsClone[orderItemIndex].skus[skuItemIndex].po_sku_mappings_v2) {
            orderItemsClone[orderItemIndex].skus[skuItemIndex].po_sku_mappings_v2 = { ship_quantity: 0 };
          }
          orderItemsClone[orderItemIndex].skus[skuItemIndex].po_sku_mappings_v2.ship_quantity = Number(newValue);
        }
        
        return orderItemsClone;
      }
    });

    dispatch(updatePOShipQty({
      field: 'poItems',
      parentIndex: poItemIndex,
      childIndex: skuIndex,
      value: Number(newValue)
    }));

    onParentSelectionChange();

    const { po_sku_mappings_v2 } = data || {};
    const { po_item_id, sku_id } = po_sku_mappings_v2 || {};

    dispatch(updatePOShipQuantity({
      ship_quantity: Number(newValue),
      po_item_id,
      sku_id
    }));
    return true;
  };

  const InfoTooltipData = ({ products_distributions }) => {
    return (
      <PoDetailsWrapper className='mt-0'>
        <div className='container-box'>
          <div className='items'>
            <span style={{ fontWeight: 'bolder' }}>Identifier / Product</span>
            <span style={{ fontWeight: 'bolder' }}>WH Stock</span>
          </div>
          {
            products_distributions?.map(({
              name,
              bundle_quantity,
              identifier_value,
              warehouse_stock
            }) => {
              return (
                <div className='items'>
                  <span>{identifier_value} / {name || '---'}</span>
                  <span>{warehouse_stock || 0} - (Pack Of: {bundle_quantity || 0})</span>
                </div>
              );
            })
          }
        </div>
      </PoDetailsWrapper>
    );
  };

  const ActionsCellRenderer = (props) => {
    return (
      <div>
        <img
          disabled={false}
          src={deleteIcon}
          alt="delete"
          className={`${'cursor-pointer'}`}
          onClick={() => {
            setSelectedSKU(props.data.id);
            setIsModalOpen(true);
          }}
        />
      </div>
    );
  };

  const columnDefs = useMemo(() => [
    {
      headerName: 'Title',
      sortable: true,
      filter: true,
      minWidth: 135,
      flex: 1,
      field: 'item_name',
      cellRenderer: ({ data }) => {
        return (
          <Tooltip
            placement="bottom"
            title={data?.item_name || ''}
          >
            <span>{data?.item_name || '---'}</span>
          </Tooltip>
        );
      }
    },
    {
      headerName: 'ASIN',
      field: 'vendorasin.asin',
      minWidth: 120,
      flex: 1
    },
    {
      headerName: 'SKU',
      field: 'sku',
      minWidth: 100,
      flex: 1
    },
    {
      headerName: 'Packed Quantity',
      minWidth: 100,
      flex: 1,
      field: 'po_sku_mappings_v2.bundle_quantity',
      cellRenderer: ({ value }) => value || 0
    },
    {
      headerName: 'Available Stock',
      minWidth: 130,
      flex: 1,
      field: 'total_quantity',
      cellRenderer: ({ value }) => value || 0
    },
    {
      headerName: 'WH Stock',
      minWidth: 130,
      flex: 1,
      field: 'warehouse_stock',
      cellRenderer: ({ data, value }) => {
        const { products_distributions } = data || {};
        return (
          <div className='card-wrapper gap-1'>
            {/* <span>{value || 0}</span> */}
            <span>{Math.max(value, 0) || 0}</span>
            <Popover
              title={null}
              content={<InfoTooltipData products_distributions={products_distributions} />}
              placement='right'>
              <img src={InfoIcon} className='info-icon-de' alt="InfoIcon" title="Info-Icon" />
            </Popover>
          </div>
        );
      }
    },
    {
      headerName: 'Ship Quantity',
      field: 'po_sku_mappings_v2.ship_quantity',
      minWidth: 110,
      flex: 1,
      editable: () => true,
      cellEditor: 'agTextCellEditor',
      valueSetter: handleUpdateShipQty,
      cellRenderer: ({ value }) => {

        // allowing enable at any stage of po
        const isDisable = false;
        return (
          <div className='edit-able-data'>
            {value || 0}
            <Tooltip placement='top' title={isDisable ? '' : 'Double click to edit'}>
              <div style={{ display: 'inline-block' }}>
                <BiSolidEdit style={isDisable ? { opacity: 0.5, pointerEvents: 'none' } : {}} />
              </div>
            </Tooltip>
          </div>
        );
      }
    },
    {
      headerName: 'Gated Status',
      minWidth: 120,
      flex: 1,
      field: 'sku_status',
      cellRenderer: ({ value }) => value?.toLowerCase() === 'non-gated' ? 'Not Gated' : value  || '---'
    },
    {
      headerName: 'Actions',
      minWidth: 120,
      flex: 1,
      cellRenderer: ActionsCellRenderer
    }
  ], [seller_skus]);

  const handleClose = () => {
    setIsModalOpen(false);
  };

  return (
    <InnerTableWrapper seller_skus={seller_skus}>
      <Box sx={{ overflow: 'auto' }}>
        <div className="ag-theme-alpine custom-grid-theme">
          <AgGridReact
            rowData={seller_skus}
            rowHeight={40}
            columnDefs={columnDefs}
            enableCellTextSelection={true}
            defaultColDef={{
              resizable: true
            }}
            headerHeight={36}
            domLayout="autoHeight"
            className="sku-details-table po-details-wrapper-table-details"
          />
          <Stack bgcolor="#fff">
            <Button
              disabled={false}
              startIcon={<FiPlus />}
              className="primary"
              text="Add SKUs"
              onClick={() => setAddSkuModal(true)}
            />
          </Stack>
        </div>
      </Box>
      {addSkuModal && <AddSkuModal
        open={addSkuModal}
        onHide={() => setAddSkuModal(false)}
        productId={product_id}
        poItemId={po_item_id}
        onSave={(id) => props?.refreshPage(id)}
      />}
      <Modal
        open={isModalOpen}
        onClose={handleClose}
        onHide={handleClose}
        closeText="Cancel"
        saveText="Confirm"
        header="Delete Confirmation"
        className="delete-confirmation-modal"
        closeButton={true}
        buttonDisabled={deletingMappingPoAndSku}
        onSave={() => {
          dispatch(DeletePoSkuMapping({ poItemId: po_item_id, poId: poId, sku_id: selectedSKU })).then(({ payload }) => {
            if (payload?.success) {
              handleClose();
              props?.refreshPage(po_item_id);
              setSelectedSKU(null);
            }
          });
        }}
      >
        <PoDetailsWrapper>
          <div className='delete-modal-wrapper'>
            <img src={WarningIcon} alt='WarningIcon' title='WarningIcon' />
            <h3>Warning</h3>
            <p>Are you sure you want to delete this mapping?</p>
          </div>
        </PoDetailsWrapper>
      </Modal>
    </InnerTableWrapper>
  );
};

export default PoRowDetails;
